import { z } from 'zod'
import './registry'

export const TenantIdSchema = z
  .string()
  .regex(/^tnt-[a-z0-9-]+$/, 'invalid tenant id')
  .openapi('TenantId', { example: 'tnt-arnoud-records' })

export const UlidSchema = z
  .string()
  .length(26)
  .openapi('Ulid', { example: '01J0X7P8K3M5N7R9V2W4Z6A8B0' })

export const ShortIdSchema = z
  .string()
  .regex(/^\d{6}$/, '6-digit numeric')
  .openapi('ShortId', { example: '427301' })

export const IsoDateSchema = z
  .string()
  .datetime({ offset: true })
  .openapi('IsoDate', { example: '2026-05-23T10:00:00.000Z' })

/** Money as a decimal-string (e.g. "19.99"). Never use JS number for money. */
export const MoneySchema = z
  .string()
  .regex(/^\d+\.\d{2}$/, 'decimal as string "dd.cc"')
  .openapi('Money', { example: '19.99' })

/**
 * Money that may legitimately go negative (e.g. a supplier payout total once a
 * return clawback exceeds that period's sales) — a decimal string, optionally
 * `-`-prefixed. Everywhere a real basket/line total lives, use `MoneySchema`
 * instead: this is only for adjustment/aggregate amounts where negative is a
 * correct value, not an error to clamp away.
 */
export const SignedMoneySchema = z
  .string()
  .regex(/^-?\d+\.\d{2}$/, 'decimal as string "dd.cc" or "-dd.cc"')
  .openapi('SignedMoney', { example: '-19.99' })

// Our own display/settlement currencies (what the shop trades in).
export const CurrencySchema = z.enum(['EUR', 'USD', 'GBP', 'JPY']).openapi('Currency')

// A generic ISO-4217 currency code, validated by shape only. Used for values
// that originate from external sources (Discogs aggregates marketplace listings
// worldwide), where the set is open-ended and a strict CurrencySchema enum would
// reject legitimate codes. Shared primitive — never inline `z.string()` here.
export const CurrencyCodeSchema = z
  .string()
  .regex(/^[A-Z]{3}$/, 'ISO-4217 currency code')
  .openapi('CurrencyCode', { example: 'USD' })

// Cursor-pagination primitives for list endpoints that page through a partition
// (the orders queue pages GSI6; future webshop endpoints will too). The catalogue
// `list` deliberately does NOT use these — it returns a single capped result set —
// so these have one consumer today, but they stay shared so every paginated surface
// agrees on the cursor wire format and limit bounds.
export const PaginationCursorSchema = z.string().base64url().optional()
export const PaginationLimitSchema = z.coerce.number().int().min(1).max(200).default(50)

export const RoleSchema = z.enum(['admin', 'assistant']).openapi('Role')

export const ErrorResponseSchema = z
  .object({
    error: z.object({
      code: z.string(),
      message: z.string(),
      details: z.unknown().optional(),
    }),
  })
  .openapi('ErrorResponse')

/**
 * Cognito JWT claim shape consumed by both the frontend auth store and the
 * backend tenant-scoping middleware. Custom attributes are `.optional()` so
 * newly-provisioned users without role/tenant don't blow up — but if a claim
 * is *present* and invalid (e.g. role outside the enum, tenant_id not in
 * `tnt-…` format), parsing fails so the caller can surface ERR_INVALID_CLAIMS.
 *
 * Output type is reshaped to the convenient `AuthProfile` form: `role` and
 * `tenantId` collapse to `null` when absent (instead of `undefined`), and
 * the `email` key is omitted entirely when not present (so JSON-serialised
 * profiles don't carry a literal `"email": undefined`).
 */
export const CognitoClaimsSchema = z
  .object({
    sub: z.string().min(1),
    email: z.string().optional(),
    'custom:role': RoleSchema.optional(),
    'custom:tenant_id': TenantIdSchema.optional(),
  })
  .transform(c => {
    const out: {
      sub: string
      email?: string
      role: z.infer<typeof RoleSchema> | null
      tenantId: z.infer<typeof TenantIdSchema> | null
    } = {
      sub: c.sub,
      role: c['custom:role'] ?? null,
      tenantId: c['custom:tenant_id'] ?? null,
    }
    if (c.email !== undefined) out.email = c.email
    return out
  })

export type TenantId = z.infer<typeof TenantIdSchema>
export type Ulid = z.infer<typeof UlidSchema>
export type ShortId = z.infer<typeof ShortIdSchema>
export type Money = z.infer<typeof MoneySchema>
export type Currency = z.infer<typeof CurrencySchema>
export type CurrencyCode = z.infer<typeof CurrencyCodeSchema>
export type Role = z.infer<typeof RoleSchema>
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>
export type AuthProfile = z.infer<typeof CognitoClaimsSchema>
